// Swiss enterprise identification number (IDE / UID / IDI): `CHE` + nine
// digits, displayed as `CHE-123.456.789`. Pure module — no Convex imports —
// so the parser is jest-testable and shared by the Zefix mapping, the salary
// certificate XML and the uniqueness check.

/**
 * `CHE-107.721.785`, `CHE107721785`, `che 107 721 785` → `CHE107721785`.
 * Anything that does not carry exactly nine digits (optionally prefixed by
 * `CHE`) is not a UID and answers undefined — legacy free-text values stay
 * in `companies.ide` untouched and are never unique-checked.
 */
export function normalizeIde(
  raw: string | undefined | null,
): string | undefined {
  if (!raw) return undefined;
  const compact = raw
    .trim()
    .toUpperCase()
    .replace(/[\s.-]/g, '');
  const match = /^(?:CHE)?(\d{9})$/.exec(compact);
  return match ? `CHE${match[1]}` : undefined;
}

/** `CHE107721785` → `CHE-107.721.785`. Non-canonical input is returned as is. */
export function formatIde(value: string): string {
  const normalized = normalizeIde(value);
  if (!normalized) return value;
  const digits = normalized.slice(3);
  return `CHE-${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}`;
}

/**
 * What every company write spreads into its patch: the display form (kept
 * verbatim when unparseable, canonically formatted otherwise) and the lookup
 * key. An empty/blank `ide` clears both.
 */
export function ideFieldsFor(ide: string | undefined): {
  ide: string | undefined;
  ideNormalized: string | undefined;
} {
  const trimmed = ide?.trim();
  if (!trimmed) return { ide: undefined, ideNormalized: undefined };
  const ideNormalized = normalizeIde(trimmed);
  return {
    ide: ideNormalized ? formatIde(ideNormalized) : trimmed,
    ideNormalized,
  };
}
